<template>
  <div v-loading="isLoading">
    <el-dialog
      :visible="modalVisible"
      width="632px"
      :before-close="closeModal"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :modal="false"
    >
      <div slot="title">
        <div class="el-dialog__title">
          {{ $t('CampaignForm_CopyFromDialog_Title') }}
        </div>
      </div>
      <div class="items-container" v-loading="modalIsLoading">
        <radio-group v-model="selectedVariant">
          <el-table
            :data="workflowsAvailableForCopy"
            :stripe="true"
            class="table-container outlined"
            :max-height="500"
          >
            <el-table-column :label="$t('CampaignForm_CopyFromDialog_Column')">
              <template slot-scope="scope">
                <radio :label="scope.row" :title="scope.row.title" />
              </template>
            </el-table-column>
          </el-table>
        </radio-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          @click="applyCopy"
          :disabled="selectedVariant === null"
        >
          <span>{{ $t('Campaign_Dialog_Button_Apply') }}</span>
          <i class="el-icon-arrow-right el-icon--right" />
        </el-button>
      </span>
    </el-dialog>
    <el-row
      v-if="model.ab && model.variants.length > 1"
      class="Workflow--Actions"
      type="flex"
      justify="end"
    >
      <el-button
        type="primary"
        icon="el-icon-copy-document"
        @click="openCopyModal"
      >
        {{ $t('CampaignForm_Button_CopyFrom') }}
      </el-button>
    </el-row>
    <iframe v-loading="workflowLoading" v-if="workflowId" :src="url" />
    <el-row class="footer">
      <el-col class="footer-ct btn-ct">
        <el-button
          :class="[
            isFramed
              ? 'nav-framed'
              : isCollapsed
              ? 'nav-collapsed'
              : 'nav-expanded'
          ]"
          v-if="workflowId && $can('updateProjectItem')"
          type="primary"
          plain
          @click="handleEdit"
          :disabled="isDisabled || !!model.mxa_migration"
        >
          <i class="el-icon-edit-outline"></i>
          {{ $t('CampaignForm_Workflow_EditWorkflow') }}
        </el-button>
        <el-button type="primary" @click="nextStep" data-qa="next-step-btn">
          {{ nextBtnLabel }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
      </el-col>
    </el-row>
    <workflow-builder
      v-if="showBuilder"
      :project-id="projectId"
      :mxa-domain="mxaDomain"
      :workflow-id="workflowId"
      :on-close="onClose"
      :new-mxa="!!model.new_mxa"
    />
  </div>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import { customConfirm, projectEndpoint } from '@/core/helpers'
import WorkflowBuilder from '@/common/components/workflow/WorkflowBuilder'
import { campaignStatus } from '@/modules/campaign-manage/enum/CampaignStatusEnum'
import { EventBus } from '@/core/EventBus'
import { $hasFeature } from '@/core/utils/VueAcl'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { cloneDeep } from 'lodash'

export default {
  name: 'Workflow',

  components: {
    WorkflowBuilder,
    RadioGroup,
    Radio
  },

  props: {
    value: {
      required: true
    },
    model: {
      type: Object,
      required: true
    },
    title: {
      required: true
    },
    variant: {
      required: false
    }
  },

  data() {
    return {
      mxaDomain: this.$store.getters.mxaDomain,
      projectId: this.$store.getters.projectId,
      workflowId: this.value.workflow_id,
      showBuilder: false,
      isDisabled: false,
      campaignStatus,
      isCollapsed: window.localStorage.getItem('navState') === 'collapsed',
      modalVisible: false,
      selectedVariant: null,
      isLoading: false,
      modalIsLoading: false,
      workflowsAvailableForCopy: [],
      workflowLoading: false
    }
  },

  created() {
    if (!this.value.workflow_id) {
      if (this.readonly) {
        return this.$message({
          message: this.$t('CampaignForm_Workflow_NotCreated'),
          type: 'warning'
        })
      }
      this.workflowLoading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: 'campaigns/create-workflow',
          data: {
            campaignId: this.model.id,
            features: {
              voucher_codes: !!this.model.voucher_codes_enabled,
              new_mxa: !!this.model.new_mxa
            }
          }
        })
      )
        .then(({ data }) => {
          this.value.workflow_id = data.id
          this.workflowId = data.id
          this.workflowLoading = false
        })
        .catch(error => {
          this.workflowLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    } else {
      if (this.hasVoucherCodes || this.model.status_canonical === 'draft') {
        this.workflowLoading = true
        RestApi(
          projectEndpoint({
            method: 'post',
            url: 'campaigns/update-workflow',
            data: {
              workflowId: this.value.workflow_id,
              features: {
                voucher_codes: !!this.model.voucher_codes_enabled,
                new_mxa: !!this.model.new_mxa
              }
            }
          })
        )
          .then(({ data }) => {
            this.workflowLoading = false
          })
          .catch(error => {
            this.workflowLoading = false
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      }
    }
  },

  mounted() {
    EventBus.$on('nav-state-change', navState => {
      this.isCollapsed = navState === 'collapsed'
    })
  },

  computed: {
    url() {
      return this.model.new_mxa
        ? `https://${this.mxaDomain}/project/${this.projectId}/workflow/${this.workflowId}/preview`
        : `https://${this.mxaDomain}/old/project/${this.projectId}/workflow/${this.workflowId}/preview`
    },

    nextBtnLabel() {
      if (this.model.trigger === campaignTriggerTypes.TRIGGER_LOCATION) {
        return this.$t('CampaignForm_Content_NextButton_Title_Locations')
      }

      if (this.model.trigger === campaignTriggerTypes.TRIGGER_EVENT) {
        return this.$t('CampaignForm_Content_NextButton_Title_Events')
      }

      return this.$t('CampaignForm_Content_NextButton_Title_Segment')
    },

    readonly() {
      return this.$route.name === 'ViewCampaign'
    },

    workflowVariants() {
      return this.model.variants
        .filter(
          v =>
            v.key !== this.variant &&
            this.model.messages[12].hasOwnProperty(v.key) &&
            this.model.messages[12][v.key].content.workflow_id
        )
        .map(v => {
          return {
            title:
              `${this.$t('MessageType_Workflow')} / ${this.$t(
                'Message_Variant'
              )}  ` + v.key,
            workflow_id: this.model.messages[12][v.key].content.workflow_id
          }
        })
    },
    hasVoucherCodes() {
      return (
        this.model.trigger === campaignTriggerTypes.TRIGGER_NONE &&
        ($hasFeature('allowVoucherCodes') || this.model.voucher_codes_enabled)
      )
    },
    isFramed() {
      return window.top !== window.self
    }
  },

  methods: {
    closeModal() {
      this.modalVisible = false
    },

    openCopyModal() {
      if (this.workflowVariants.length === 0) {
        this.modalVisible = true
      } else {
        this.modalIsLoading = true
        RestApi(this.getWorkflowStatsEndpoint())
          .then(({ data }) => {
            this.workflowsAvailableForCopy = this.workflowVariants.filter(v =>
              (data || []).includes(v.workflow_id)
            )
            this.modalVisible = true
            this.modalIsLoading = false
          })
          .catch(error => {
            this.workflowsAvailableForCopy = []
            this.modalVisible = true
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.modalIsLoading = false
          })
      }
    },

    applyCopy() {
      this.closeModal()
      this.isLoading = true
      RestApi(this.copyWorkflowEndpoint(this.selectedVariant.workflow_id))
        .then(({ data }) => {
          const messages = cloneDeep(this.model.messages)

          messages[12][this.variant].content.workflow_id = data.response.id

          this.$set(this.model, 'messages', messages)
          this.workflowId = data.response.id
          this.isLoading = false
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
          this.isLoading = false
        })
    },

    copyWorkflowEndpoint(workflowId) {
      return projectEndpoint({
        method: 'post',
        url: `campaigns/copy-workflow-for-variant`,
        data: {
          workflow_id: workflowId
        }
      })
    },

    getWorkflowStatsEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `campaigns/check-workflows`,
        data: {
          workflow_ids: this.workflowVariants.map(v => v.workflow_id)
        }
      })
    },

    nextStep() {
      EventBus.$emit('campaign-create-next-step')
    },

    onClose() {
      this.showBuilder = false
    },

    handleEdit() {
      if (
        this.model.status === campaignStatus.RUNNING ||
        this.model.status === campaignStatus.CLOSED
      ) {
        customConfirm(
          this.$t('Campaign_Dialog_WorkflowEdit_Title'),
          this.$t('Campaign_Dialog_WorkflowEdit_Message'),
          this.$t('Campaign_Dialog_Button_Continue'),
          this.$t('Campaign_Dialog_Button_Cancel')
        ).then(() => {
          this.isDisabled = true
          RestApi(
            projectEndpoint({
              method: 'post',
              url: `campaigns/${this.model.id}/pause`
            })
          )
            .then(({ data }) => {
              this.model.status = data.statusCode
              this.isDisabled = false
              this.showBuilder = true
            })
            .catch(error => {
              this.$message({
                message: error.message,
                type: 'error'
              })
              this.isDisabled = false
            })
        })
        return
      }

      this.showBuilder = true
    }
  }
}
</script>

<style scoped>
button {
  margin-top: 20px;
}

iframe {
  width: 100%;
  height: 700px;
  border: 1px solid #d6d8db;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
  /** 85 and 325 **/
}

.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}

.edit-workflow-btn {
}

.nav-framed {
  margin-left: 20px;
}

.nav-collapsed {
  margin-left: 85px;
}

.nav-expanded {
  margin-left: 325px;
}

.btn-ct {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.Workflow--Actions {
  padding: 0 8px 10px;
}

.Workflow--Actions button {
  margin-top: 0;
}

.table-container.outlined {
  border: 1px solid #dbdee7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/channels/Workflow.vue